import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Send from 'lucide-react/dist/esm/icons/send';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import ClipboardList from 'lucide-react/dist/esm/icons/clipboard-list';
import { surveyApi } from '../../services/api/survey';
import { useUIStore } from '../../stores/uiStore';
import { getTranslation } from '../../i18n';
import { Button, TextArea, TextField, Text, Spinner } from '../../components/ui';
import type { SurveyRespondContext } from './types';

const SurveyResponder: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const isPublicLink = location.pathname.startsWith('/survey/p/');
  const { language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);

  const [context, setContext] = useState<SurveyRespondContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) return;
    const fetcher = isPublicLink
      ? surveyApi.getPublicContext(token)
      : surveyApi.getRespondContext(token);

    fetcher
      .then(({ data }) => {
        setContext(data);
        if (data.already_completed) setSubmitted(true);
      })
      .catch(() => setError(t('surveyResponderErrorUnavailable')))
      .finally(() => setLoading(false));
  }, [token, isPublicLink]);

  const updateAnswer = useCallback((questionId: string, text: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: text }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!token || !context) return;

    const answerPayload = context.questions
      .map((q) => ({ question_id: q.id, answer_text: answers[q.id] || '' }))
      .filter((a) => a.answer_text.trim());

    if (answerPayload.length === 0) return;

    setSubmitting(true);
    try {
      if (isPublicLink) {
        await surveyApi.submitPublicResponse(token, {
          name: name.trim(),
          email: email.trim(),
          answers: answerPayload,
        });
      } else {
        await surveyApi.submitResponse(token, {
          answers: answerPayload,
          name: name.trim() || undefined,
        });
      }
      setSubmitted(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'string' && detail.includes('email already')) {
        setError(t('surveyResponderErrorDuplicateEmail'));
      } else {
        setError(t('surveyResponderErrorSubmitFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [token, context, answers, name, email, isPublicLink]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-4">
          <ClipboardList size={48} className="mx-auto text-content-muted opacity-40" />
          <Text variant="subheading" className="text-content">{error}</Text>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-4">
          <CheckCircle size={48} className="mx-auto text-emerald-400" />
          <Text variant="subheading" className="text-content">{t('surveyResponderThankYou')}</Text>
          <Text variant="body" className="text-content-muted">
            {t('surveyResponderResponseRecorded')}
          </Text>
        </div>
      </div>
    );
  }

  if (!context) return null;

  const allAnswered = context.questions.every((q) => answers[q.id]?.trim());
  const canSubmit = allAnswered
    && name.trim().length > 0
    && (!isPublicLink || (email.trim().length > 0 && email.includes('@')));

  return (
    <div className="h-screen overflow-y-auto bg-surface-page">
      <header className="px-6 py-4 border-b border-subtle sticky top-0 bg-surface-page z-sticky">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand/10">
            <ClipboardList size={20} className="text-primary" />
          </div>
          <h1 className="text-lg font-semibold text-content tracking-tight">
            {context.title || t('surveyResponderFallbackTitle')}
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Identity fields */}
        <div className="space-y-4 pb-4 border-b border-subtle">
          {isPublicLink && (
            <TextField
              label={t('surveyResponderLabelEmail')}
              value={email}
              onChange={(e) => setEmail(typeof e === 'string' ? e : e.target.value)}
              placeholder={t('surveyResponderPlaceholderEmail')}
            />
          )}
          <TextField
            label={t('surveyResponderLabelName')}
            value={name}
            onChange={(e) => setName(typeof e === 'string' ? e : e.target.value)}
            placeholder={t('surveyResponderPlaceholderName')}
          />
        </div>

        {/* Questions */}
        {context.questions.map((q, idx) => (
          <div key={q.id} className="space-y-2">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-surface-secondary text-content-muted text-xs font-medium">
                {idx + 1}
              </span>
              <Text variant="body" className="text-content font-medium pt-0.5">
                {q.question_text}
              </Text>
            </div>
            <div className="pl-10">
              <TextArea
                value={answers[q.id] || ''}
                onChange={(e) => updateAnswer(q.id, e.target.value)}
                placeholder={t('surveyResponderPlaceholderAnswer')}
                rows={3}
              />
            </div>
          </div>
        ))}

        <div className="pt-4 border-t border-subtle">
          <Button
            variant="primary"
            size="lg"
            leftIcon={<Send size={16} />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
          >
            {t('surveyResponderBtnSubmit')}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default React.memo(SurveyResponder);
